import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import cmsAccordionBlockItemObject from '@/entities/cmsAccordionBlockItem/interface'

import {
  ALL_ACCORDION_BLOCKS_QUERY,
  SINGLE_ACCORDION_BLOCK_QUERY,
  CREATE_ACCORDION_BLOCK_MUTATION,
  UPDATE_ACCORDION_BLOCK_MUTATION,
  DELETE_ACCORDION_BLOCK_MUTATION,
  FORCE_DELETE_ACCORDION_BLOCK_MUTATION,
  ADD_ACCORDION_BLOCK_ITEMS_MUTATION,
  REMOVE_ACCORDION_BLOCK_ITEMS_MUTATION,
  CREATE_ACCORDION_BLOCK_ITEMS_MUTATION,
  DELETE_ACCORDION_BLOCK_ITEMS_MUTATION,
  UPDATE_ACCORDION_BLOCK_ITEMS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsAccordionBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_ACCORDION_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.accordionBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_ACCORDION_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.accordionBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_ACCORDION_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.accordionBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_ACCORDION_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.accordionBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_ACCORDION_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.accordionBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_ACCORDION_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.accordionBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_ACCORDION_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsAccordionBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_ACCORDION_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsAccordionBlock
    })
  },

  assignItems: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        itemIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_ACCORDION_BLOCK_ITEMS_MUTATION, variables)
  },

  unassignItems: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        itemIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_ACCORDION_BLOCK_ITEMS_MUTATION, variables)
  },

  createItems: async (id: string, items: Array<cmsAccordionBlockItemObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        items: items,
      },
    }
    return graphQLClient.request(CREATE_ACCORDION_BLOCK_ITEMS_MUTATION, variables)
  },

  deleteItems: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        itemIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_ACCORDION_BLOCK_ITEMS_MUTATION, variables)
  },

  updateItemsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        itemIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_ACCORDION_BLOCK_ITEMS_ORDER_MUTATION, variables)
  },
}

export default cmsAccordionBlockRepository
